import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Paper, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../../../components/loader';
import StatusChip from '../../../../components/statusChip';
import NotificationSnackbar from '../../../../components/notificationSnackbar';
import {
    CreateEditBackButton,
    CreateEditPageTitleBlock,
    createEditCancelButtonSx,
    createEditPageHeaderSx,
    createEditPageShellSx,
    createEditPrimaryButtonSx,
} from '../../../../styles/pageFormStyles';
import CategorySection from './categorySection';
import { ekScoreTabPath } from '../../ekScoreConfiguration/ekScoreConfiguration.paths';
import {
    VERSION_STATUS,
    cloneDocument,
    countOptions,
    countQuestions,
    effectiveWeights,
    formatWeight,
    getVersion,
    globalWeightTotal,
    publishVersion,
    saveVersionDocument,
    validateDocument,
} from '../questionnaire.service';

const statSx = {
    border: '1px solid #DFE5F6',
    borderRadius: '16px',
    px: 2.5,
    py: 2,
    minWidth: 140,
    backgroundColor: '#FFFFFF',
};

function Stat({ label, value }) {
    return (
        <Box sx={statSx}>
            <Typography sx={{ fontSize: 12, letterSpacing: '0.4px', textTransform: 'uppercase', color: '#625B71', fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#1C1B1F' }}>{value}</Typography>
        </Box>
    );
}

/**
 * One catalogue version: read-only once published, editable while it is a draft.
 *
 * Editing is gated on status rather than on a toggle because the constraint is real — a stored
 * assessment names the catalogue version it was scored against, so a published version's questions
 * and risk values have to stay exactly as they were. Changing the questionnaire means publishing a
 * new version, which is what the Duplicate action on the list produces.
 */
export default function QuestionnaireVersionPage() {
    const navigate = useNavigate();
    const { version } = useParams();

    const [record, setRecord] = useState(null);
    const [document, setDocument] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [issues, setIssues] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
    const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

    const load = useCallback(async () => {
        setLoading(true);
        const result = await getVersion(version);
        if (result.success) {
            setRecord(result.data);
            setDocument(cloneDocument(result.data.document));
            setDirty(false);
            setIssues([]);
        } else {
            setRecord(null);
            setDocument(null);
        }
        setLoading(false);
    }, [version]);

    useEffect(() => {
        load();
    }, [load]);

    const editable = record?.status === VERSION_STATUS.DRAFT;
    const weights = useMemo(() => effectiveWeights(document), [document]);
    const weightTotal = globalWeightTotal(document);

    const updateCategory = (index, next) => {
        const categories = [...document.categories];
        categories[index] = next;
        setDocument({ ...document, categories });
        setDirty(true);
    };

    const removeCategory = (index) => {
        setDocument({ ...document, categories: document.categories.filter((_, i) => i !== index) });
        setDirty(true);
    };

    const addCategory = () => {
        setDocument({
            ...document,
            categories: [
                ...(document.categories || []),
                { categoryCode: '', label: '', globalWeightPct: 10, questions: [] },
            ],
        });
        setDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);
        const result = await saveVersionDocument(version, document);
        setSaving(false);

        if (!result.success) {
            notify(result.error.message, 'error');
            return;
        }
        setRecord(result.data);
        setDirty(false);
        setIssues(validateDocument(document));
        notify(`${version} saved.`);
    };

    const handlePublish = async () => {
        // Save first: publishing what is on screen rather than what was last written avoids
        // publishing a version that silently differs from the edits in front of the admin.
        if (dirty) {
            const saved = await saveVersionDocument(version, document);
            if (!saved.success) {
                notify(saved.error.message, 'error');
                return;
            }
            setDirty(false);
        }

        const found = validateDocument(document);
        if (found.length) {
            setIssues(found);
            notify('Fix the listed problems before publishing.', 'error');
            return;
        }

        const result = await publishVersion(version);
        if (!result.success) {
            notify(result.error.message, 'error');
            return;
        }
        notify(`${version} is now the current catalogue.`);
        navigate(ekScoreTabPath('questionnaire'));
    };

    if (loading) return <Loader />;

    if (!record) {
        return (
            <Box sx={createEditPageShellSx}>
                <CreateEditBackButton onClick={() => navigate(ekScoreTabPath('questionnaire'))} icon={<ArrowBackIcon />} />
                <Alert severity="error" sx={{ borderRadius: '16px' }}>
                    Version {version} was not found.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={createEditPageShellSx}>
            <Box sx={createEditPageHeaderSx}>
                <CreateEditBackButton onClick={() => navigate(ekScoreTabPath('questionnaire'))} icon={<ArrowBackIcon />}>
                    Back to versions
                </CreateEditBackButton>

                <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <CreateEditPageTitleBlock
                        title={`Questionnaire ${record.version}`}
                        subtitle={
                            record.changeNote ||
                            (record.basedOn ? `Copied from ${record.basedOn}.` : 'Initial catalogue.')
                        }
                    />
                    <StatusChip
                        status={record.status === VERSION_STATUS.CURRENT ? 'ACTIVE' : record.status}
                        labelOverride={
                            record.status === VERSION_STATUS.CURRENT
                                ? 'Current'
                                : record.status === VERSION_STATUS.DRAFT
                                    ? 'Draft'
                                    : 'Retired'
                        }
                    />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Stat label="Categories" value={document.categories?.length || 0} />
                <Stat label="Questions" value={countQuestions(document)} />
                <Stat label="Answers" value={countOptions(document)} />
                <Stat label="Global weight total" value={formatWeight(weightTotal)} />
            </Box>

            {!editable && (
                <Alert severity="info" sx={{ borderRadius: '16px', mb: 3 }}>
                    <AlertTitle sx={{ fontWeight: 700 }}>Read only</AlertTitle>
                    A published version cannot be changed — assessments scored against {record.version} refer to
                    these exact questions. Use <strong>Duplicate</strong> on the versions list to start a new draft
                    from it.
                </Alert>
            )}

            {issues.length > 0 && (
                <Alert severity="error" sx={{ borderRadius: '16px', mb: 3 }}>
                    <AlertTitle sx={{ fontWeight: 700 }}>
                        {issues.length} problem{issues.length === 1 ? '' : 's'} to fix before publishing
                    </AlertTitle>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {issues.slice(0, 8).map((issue) => (
                            <li key={issue}>{issue}</li>
                        ))}
                    </Box>
                </Alert>
            )}

            {document.scale?.note && (
                <Paper
                    elevation={0}
                    sx={{ p: 2.5, borderRadius: '16px', border: '1px solid #E7ECF2', mb: 3, backgroundColor: '#FFFFFF' }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#294190', mb: 0.5 }}>
                        Scale {document.scale.riskValueRange ? `(${document.scale.riskValueRange.join('–')})` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#5B6472' }}>{document.scale.note}</Typography>
                </Paper>
            )}

            <Paper
                elevation={0}
                sx={{ p: 2.5, borderRadius: '16px', border: '1px solid #E7ECF2', mb: 3, backgroundColor: '#FFFFFF' }}
            >
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#294190', mb: 0.5 }}>Weighting</Typography>
                {document.weighting?.globalWeightPct && (
                    <Typography sx={{ fontSize: 14, color: '#5B6472', mb: 0.5 }}>
                        <strong>Global weight:</strong> {document.weighting.globalWeightPct}
                    </Typography>
                )}
                {document.weighting?.intraCategoryWeight && (
                    <Typography sx={{ fontSize: 14, color: '#5B6472', mb: 0.5 }}>
                        <strong>Weight in category:</strong> {document.weighting.intraCategoryWeight}
                    </Typography>
                )}
                <Typography sx={{ fontSize: 14, color: '#5B6472' }}>
                    The global weights in this version total {formatWeight(weightTotal)}, so a category's share of
                    the score is its global weight ÷ {formatWeight(weightTotal)}. A question's share of its category
                    is its weight ÷ the sum of that category's question weights. The risk engine scores with exactly
                    these weights.
                </Typography>
            </Paper>

            {(document.categories || []).map((category, index) => (
                <CategorySection
                    key={`${category.categoryCode}-${index}`}
                    category={category}
                    editable={editable}
                    weightTotal={weightTotal}
                    effectivePct={weights[category.categoryCode] || 0}
                    onChange={(next) => updateCategory(index, next)}
                    onRemove={() => removeCategory(index)}
                />
            ))}

            {editable && (
                <Button
                    onClick={addCategory}
                    startIcon={<Icon icon="tabler:plus" width="18" height="18" />}
                    sx={{ textTransform: 'none', color: '#294190', fontWeight: 600, mb: 2 }}
                >
                    Add category
                </Button>
            )}

            {editable && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        mt: 4,
                        flexWrap: 'wrap',
                    }}
                >
                    <Button onClick={() => navigate(ekScoreTabPath('questionnaire'))} sx={createEditCancelButtonSx}>
                        Cancel
                    </Button>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {dirty && (
                            <Typography sx={{ fontSize: 14, color: '#8A5A00' }}>Unsaved changes</Typography>
                        )}
                        <Button
                            variant="outlined"
                            disabled={saving || !dirty}
                            onClick={handleSave}
                            sx={{
                                ...createEditPrimaryButtonSx,
                                bgcolor: 'transparent',
                                color: '#294190',
                                border: '1px solid #294190',
                                '&:hover': { bgcolor: '#EEF1FA', boxShadow: 'none' },
                            }}
                        >
                            {saving ? 'Saving…' : 'Save draft'}
                        </Button>
                        <Button variant="contained" onClick={handlePublish} sx={createEditPrimaryButtonSx}>
                            Publish version
                        </Button>
                    </Box>
                </Box>
            )}

            <NotificationSnackbar
                open={snackbar.open}
                onClose={closeSnackbar}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Box>
    );
}
